import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { createWidget } from 'actions/editor';
import { useRouteMatch, useHistory, useParams } from "react-router-dom";

import { contentGrid, ContentGridPalette } from './contentGrid';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { MIXPANEL_WIDGET_USED } from 'constants/mixpanelEvents';
import { createMixpanelEvent } from 'actions/mixpanel';
import {
  IMAGE, VIDEO, BUTTON, TEXT, SHAPE, LINE, RECTANGLE,
  FORM, IFRAME, HTML, ANCHOR, MENU,
  SECTION, SECTION_COLUMN,
  CONTENT, EXTERNAL_FORM, FORM_STEP, CAROUSEL, ASSET, SEEK,
  LIVE_ELEMENT,
  CHARTS
} from 'constants/widgetTypes';
import { getBaseUrl } from "services/util";

import _, { includes } from 'lodash';
import cx from 'classnames';
import shortid from 'shortid';
import { Nav, Tab, TabContainer, Button, Image, Fade } from 'react-bootstrap';

import StoryboardContext from 'components/Storyboard/context';
import { getPositionForNewWidget } from 'components/Editor/helper/layoutManager';

import { forms, FormsPalette } from './form';
import { MenuPalette, menus } from './menu';
import { CarouselPalette, carousels } from './carousel';

import { SectionPalette, sections } from './section'

import styles from './palette.module.scss';
import { fetchCreatedForms } from 'actions/forms/forms';
import InfiniteScroll from 'components/InfiniteScroll';
import Tooltip from 'components/Tooltip';
import Truncate from "react-truncate";
import { inlineAssetCards } from './inlineAsset';

import { INTEGRATION_TYPES } from 'constants/integrationList';
import { PALETTE_CONSTANT } from 'constants/palette';

import { TextPalette } from './text';
import { MediaPalette } from './media';
import { ButtonPalette } from './button';
import { ShapePalette } from './shapes';
import { SeekPalette } from './seek'
import { EmbedPalette } from './embed';
import { LiveElementsPalette } from './liveElements';
import { toast } from 'react-toastify';
import { FailureToast } from 'components/Toast';
import { Property } from 'components/Editor/components/properties';

export const PaletteContext = React.createContext({
  close: () => { }
});

export const usePaletteSelector = (editorSession, type) => {
  const palettes = useSelector(state => _.get(state, 'palette.items', []));
  return palettes.find(item => item.type === type);
}

export const usePalettesSelector = () => {
  const defaultPalettes = useSelector(state => _.get(state, 'palette.default.items', []));
  const brandkitPalettes = useSelector(state => _.get(state, 'palette.brandkit.items', []));

  const palettes = defaultPalettes.map(item => {
    const brandkitItem = brandkitPalettes.find(brandkitItem => brandkitItem.type === item.type);
    return  {
      name: item.name,
      type: item.type,
      defaultId: item.id,
      brandkitId: _.get(brandkitItem, 'id', null),
      showDefault: !_.get(brandkitItem, 'enabled', true),
    }
  });

  return palettes;
}

export function Palette({ open, style, onClose, editorSession, defaultParent, storyboardType, editorType, target, ...rest }) {
  const { id } = useParams();

  const progressive = useSelector(state => _.get(state, `gatings.gating.${id}.item.progressive`, false));
  const formsLength = useSelector(state => _.get(state, `${editorSession}.present.widgets`, []).filter(widget => widget.type === FORM).length);
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));

  const [paletteContext, setPaletteContext] = React.useState({
    close: () => {
      onClose();
    }
  });

  const palettes = usePalettesSelector();

  const [activePane, setActivePane] = useState('text');

  const paletteSection1 = () => {
    const section1Types = PALETTE_CONSTANT.filter(item => item.priority === 'FIRST').map(item => {
      if(storyboardType == ""){
        return item.type;
      }

      if(_.includes(item.storyboardTypes, storyboardType)){
        return item.type
      }
      
    });

    return palettes.filter(item => section1Types.includes(item.type));
  }

  const paletteSection2 = () => {
    const section2Types = PALETTE_CONSTANT.filter(item => item.priority === 'SECOND' && item.editors.includes(editorType)).map(item => {
      if(storyboardType == ""){
        return item.type;
      }

      if(_.includes(item.storyboardTypes, storyboardType)){
        return item.type
      }
    });
    return palettes.filter(item => section2Types.includes(item.type));
  }

  useEffect(() => {
    if(palettes.length > 0) {
      setPaletteContext(paletteContext => {
        return {
          ...paletteContext,
          palettes: palettes
        }
      })
    }
  }, [palettes.length]);


  const handleBlockedClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toast(<FailureToast heading={`Error`} description={`Progressive gating is enabled. Please disable it to add more forms`} />, {
      type: toast.TYPE.DEFAULT,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 3000,
    });
  };

  return (
    <PaletteContext.Provider value={paletteContext}>
      <TabContainer id="widget_type_tabs" defaultActiveKey={'TEXT'} transition={Fade} onSelect={(key) => {
        setActivePane(key);
      }}>
        <div className={styles.tabContainer}>
          <div className={styles.widgetTypes}>
            <Nav className={'flex-column'}>
              {paletteSection1().map((item) => {
                return (
                  <Nav.Item>
                    <Nav.Link eventKey={item.type}>{item.name}</Nav.Link>
                  </Nav.Item>
                );
              })}

              <div className={styles.paletteSeperator} />

              {paletteSection2().map((item) => {
                const block = progressive && item.type === "FORMS" && formsLength >= 1 && entityType === "GATING";

                return block ? (
                  <Nav.Item>
                    <Nav.Link onClick={handleBlockedClick}>
                      {item.name}
                    </Nav.Link>
                  </Nav.Item>
                ) : (
                  <Nav.Item>
                    <Nav.Link eventKey={item.type}>{item.name}</Nav.Link>
                  </Nav.Item>
                );
              })}

              <div className={styles.paletteSeperator} />

              {!_.includes(['SPATIAL'], storyboardType) && !_.includes(['GATING', 'LIVE_ELEMENT', 'PALETTE_COMPONENT'], editorType) &&
                <Nav.Item>
                  <Nav.Link eventKey={'ANCHOR'}>Anchor</Nav.Link>
                </Nav.Item>
              }
              {editorType !== 'LIVE_ELEMENT' && 
                <Nav.Item>
                  <Nav.Link eventKey={'LIVE_ELEMENT'}>Live Elements</Nav.Link>
                </Nav.Item>
              }
            </Nav>
          </div>

          <Tab.Content className={styles.tabContent}>
            <TextPalette editorSession={editorSession} defaultParent={defaultParent} />
            <MediaPalette editorSession={editorSession} defaultParent={defaultParent} target={target}/>
            <ButtonPalette editorSession={editorSession} defaultParent={defaultParent} />
            <ShapePalette editorSession={editorSession} defaultParent={defaultParent} />
            <CarouselPalette editorSession={editorSession} defaultParent={defaultParent} />
            <ChartPalette editorSession={editorSession} defaultParent={defaultParent} />
            <SeekPalette editorSession={editorSession} defaultParent={defaultParent} />
            <EmbedPalette editorSession={editorSession} defaultParent={defaultParent} />
            <ContentGridPalette editorSession={editorSession} defaultParent={defaultParent}/>
            <SectionPalette editorSession={editorSession} defaultParent={defaultParent} />
            <FormsPalette editorSession={editorSession} defaultParent={defaultParent} />
            <LiveElementsPalette editorSession={editorSession} defaultParent={defaultParent} active={activePane === 'liveElements'} />
            <MenuPalette editorSession={editorSession} defaultParent={defaultParent} active={activePane === 'Menu'} />
            <AnchorPalette editorSession={editorSession} defaultParent={defaultParent} />
          </Tab.Content>
        </div>
      </TabContainer>
    </PaletteContext.Provider>
  )
}

const CarouselPalette1 = ({ editorSession, defaultParent }) => {
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const parentLayout = _.get(parentWidget, 'properties.desktop.layout');

  const handleCreateCarousel = (data) => {
    const position = getPositionForNewWidget(editorSession, parentLayout, { width: 1200, height: 250 });
    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: CAROUSEL,
            name: "Carousel",
            properties: {
              ...data.properties,
              desktop: {
                ...data.properties.desktop,
                visible: true,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `${position.x}px, ${position.y}px`,
                    rotate: "0deg"
                  }
                }
              },
              mobile: {
                ...data.properties.mobile,
                visible: true,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: "0px, 20px",
                    rotate: "0deg",
                  }
                }
              },
            },
            widgets: data.widgets,
            content: data.content
          },
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }

  return (
    <Tab.Pane eventKey="CAROUSELS" className={cx(styles.tabPane, styles.carouselPalette)}>
      <div className={styles.paletteTitle}>
        <h3 className={styles.label}>{'CLEVERSTORY'}</h3>
        <hr className={styles.labelHighliter} />
      </div>
      {carousels.map((carousel, index) => {
        return (
          <div key={index} className={styles.widget} onClick={() => handleCreateCarousel(carousel.data)}>
            {carousel.render()}
          </div>
        );
      })}
    </Tab.Pane>
  );
}


const SectionPalette1 = ({ editorSession, defaultParent }) => {
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);

  const storyboardContext = React.useContext(StoryboardContext);
  const interactiveContent = _.get(storyboardContext, 'type') === 'INTERACTIVE_CONTENT';
  const spatialContent = _.get(storyboardContext, 'type') === 'SPATIAL';
  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const breakpoint = useSelector(state => { return _.get(state, `${editorSession}.present.breakpoint`) })
  const parentLayout = _.get(parentWidget, `properties.${breakpoint}.layout`);

  const handleCreateSection = (data) => {
    const position = getPositionForNewWidget(editorSession, parentLayout, { width: 1200, height: 50 });
    const uid = `widget_${shortid.generate()}`;

    dispatch(
      createWidget(
        editorSession,
        [
          {
            ...data,
            uid: uid,
            properties: {
              desktop: {
                visible: true,
                ...data.properties.desktop,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `0px, ${position.y}px`,
                    rotate: "0deg",
                  }
                },
                stretched: data.properties.desktop.stretched && !interactiveContent && !spatialContent
              },
              mobile: {
                ...data.properties.mobile,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: `0px, ${position.y}px`,
                    rotate: "0deg",
                  }
                }
              }
            },
            widgets: data.widgets.map(widget => {
              if (widget.type === SECTION_COLUMN) {
                const parentDesktopWidth = _.get(parentWidget, 'properties.desktop.container.minWidth');
                const parentMobileWidth = _.get(parentWidget, 'properties.mobile.container.minWidth');

                const desktopWidthPercent = parseFloat(_.get(widget, 'properties.desktop.layout.width'));
                const desktopWidth = desktopWidthPercent * parentDesktopWidth / 100;

                widget = _.cloneDeep(widget);
                _.set(widget, 'properties.desktop.container.minWidth', desktopWidth);
                _.set(widget, 'properties.mobile.container.minWidth', parentMobileWidth);

                return widget;
              }

              return widget;
            })
          }
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }

  return (
    <Tab.Pane eventKey="SECTIONS" className={cx(styles.tabPane, styles.textPalette)}>
      {sections.map(section => {
        return (
          <div className={styles.widget} key={section.id} onClick={() => {
            handleCreateSection(section.data);
          }}>
            {section.render()}
          </div>
        );
      })}
    </Tab.Pane>
  );
}

const ContentGridPalette1 = ({ editorSession, defaultParent }) => {
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });
  const breakpoint = useSelector(state => { return _.get(state, `${editorSession}.present.breakpoint`) })

  const parentLayout = _.get(parentWidget, `properties.${breakpoint}.layout`);

  const handleContentGrid = (contentGridRequest) => {
    var data = _.cloneDeep(contentGridRequest);
    const position = getPositionForNewWidget(editorSession, parentLayout, { width: 260, height: 260 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: CONTENT,
            name: "Content",
            properties: {
              ...data.properties,
              desktop: {
                ...data.properties.desktop,
                visible: true,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `0px, ${position.y}px`,
                    rotate: "0deg"
                  }
                },
              },
              mobile: {
                ...data.properties.mobile,
                visible: true,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: `0px, ${position.y}px`,
                    rotate: "0deg",
                  }
                },
              },
            },
            widgets: [...data.widgets],
            content: data.content
          },
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }

  const handleInlineAsset = (assetRequest) => {
    var data = _.cloneDeep(assetRequest);
    const position = getPositionForNewWidget(editorSession, parentLayout, { width: 260, height: 260 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: ASSET,
            name: "Inline Asset",
            properties: {
              ...data.properties,
              desktop: {
                ...data.properties.desktop,
                visible: true,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `0px, ${position.y}px`,
                    rotate: "0deg"
                  }
                }
              },
              mobile: {
                ...data.properties.mobile,
                visible: true,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: "0px, 0px",
                    rotate: "0deg",
                  }
                }
              },
            },
            content: data.content
          },
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }

  return (
    <Tab.Pane eventKey="ASSET_GRID" className={cx(styles.tabPane, styles.contentGridPalette)}>
      <div className={styles.assetcardManagement}>
        <Tab.Container id="asset-management-tab" defaultActiveKey="static">
          <header className={styles.assetCardManagementHeader}>
            <Nav variant="pills" className={styles.assetCardManagementTab}>
              <Nav.Item>
                <Nav.Link eventKey="static">Static Cards</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="inline">In-Line Cards</Nav.Link>
              </Nav.Item>
            </Nav>
          </header>
          <Tab.Content className={styles.assetContentContainer}>
            <Tab.Pane className={styles.staticCards} eventKey="static">
              <div>
                {contentGrid.map((card) => {
                  return (
                    <div className={styles.cardHover} key={card.id} onClick={() => { handleContentGrid(card.data) }} >
                      {card.render()}
                    </div>
                  )
                })}
              </div>
            </Tab.Pane>
            <Tab.Pane className={styles.inlineCards} eventKey="inline">
              {inlineAssetCards.map((card, index) => {
                return (
                  <div className={styles.inlinecardHover} onClick={() => { handleInlineAsset(card.data) }} key={card.id}>
                    <span className={styles.title} >{`${(index + 1)}.`} {card.name}</span>
                    {card.render()}
                  </div>
                )
              })}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </Tab.Pane>
  )
}

const FormsPalette1 = ({ editorSession, defaultParent }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const loading = useSelector(state => _.get(state, `forms.loading`, false));
  const pagination = useSelector(state => _.get(state, 'forms.pagination', {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;
  const integrationTypes = _.filter(INTEGRATION_TYPES, { forms: true });
  const activatedIntegrations = useSelector(state => _.get(state, 'integrations.activated.items', []));
  const customFormFields = useSelector((state) => _.get(state, "formFields.items", null));

  const sortIntegrations = () => {
    const integrationOrder = _.map(integrationTypes, 'type');
    const filteredList = _.filter(activatedIntegrations, item => _.includes(integrationOrder, item.type))

    return _.sortBy(filteredList, item => _.indexOf(integrationOrder, item.type));
  };

  let activeIntegrations = sortIntegrations();

  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const createdForms = useSelector(state => _.get(state, `forms.items`, null));

  const paletteContext = React.useContext(PaletteContext);

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const desktopParentLayout = _.get(parentWidget, `properties.desktop.layout`);
  const mobileParentLayout = _.get(parentWidget, `properties.mobile.layout`);

  const updateWidgets = (formWidgets, customFormFields) => {

    let fields = customFormFields.filter(field => !field.userDefined);
    let updatedArray = _.cloneDeep(formWidgets);

    _.forEach(updatedArray, item => {
      _.forEach(item.widgets, widget => {
        let matchingWidget = _.find(fields, { name: widget.name });
        if (matchingWidget) {
          _.set(widget, 'content', matchingWidget.id);
        }
      });
    });

    return updatedArray;
  }

  const handleCreateForm = (data) => {
    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: 600, height: 415 });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 335, height: 463 });
    const updatedWidgets = updateWidgets(data.widgets, customFormFields);

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: FORM,
            name: "Form",
            properties: {
              ...data.properties,
              desktop: {
                ...data.properties.desktop,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                    rotate: "0deg"
                  }
                }
              },
              mobile: {
                ...data.properties.mobile,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                    rotate: "0deg",
                  }
                }
              },
            },
            widgets: updatedWidgets,
            content: data.content
          },
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }

  const handleManageForms = () => {
    const url = `${baseUrl}/integration-settings/forms`;
    history.push(url);
  }

  const production = process.env.NODE_ENV === 'production';
  const imageId = production ? "62e3ab81dbfd3e6ec4a2a133" : "62de5538833d8a337666b497";

  const handleCreateExternalForm = (form, data) => {

    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: 600, height: 415 });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 335, height: 463 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: EXTERNAL_FORM,
            name: "Form",
            properties: {
              desktop: {
                visible: true,
                layout: {
                  transform: {
                    translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                    rotate: "0deg"
                  },
                  width: '600px',
                  height: `415px`
                },
                border: null,
                background: {
                  r: 245,
                  g: 245,
                  b: 245,
                  a: 1
                }
              },
              mobile: {
                visible: true,
                layout: {
                  transform: {
                    translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                    rotate: "0deg"
                  },
                  width: '335px',
                  height: `463px`
                }
              }
            },
            order: 1,
            widgets: [{
              type: FORM_STEP,
              name: "Success",
              properties: {
                desktop: {
                  visible: true,
                  layout: {
                    transform: {
                      translate: `0px, 0px`,
                      rotate: "0deg"
                    }
                  }
                },
                mobile: {
                  visible: true,
                  layout: {
                    transform: {
                      translate: `0px, 0px`,
                      rotate: "0deg"
                    }
                  }
                }
              },
              widgets: [
                {
                  type: TEXT,
                  name: "Form Title",
                  properties: {
                    "desktop": {
                      "visible": true,
                      "layout": {
                        "transform": {
                          "translate": "30px,268px",
                          "rotate": "0deg",
                          "scale": "1,1"
                        },
                        "width": "353.461px",
                        "height": "auto"
                      },
                      "fontSize": "16",
                      "lineSpacing": "1.2",
                      "letterSpacing": "0",
                      "color": null,
                      "alignment": "left",
                      "animation": null
                    },
                    "mobile": {
                      "visible": true,
                      "layout": {
                        "transform": {
                          "translate": "20px,336.00048828125px",
                          "rotate": "0deg",
                          "scale": "1,1"
                        },
                        "width": "261px",
                        "height": "auto"
                      },
                      "fontSize": "32",
                      "lineSpacing": "1.2",
                      "letterSpacing": "0",
                      "color": null,
                      "alignment": "left",
                      "animation": null
                    }
                  },
                  content: '<h3><strong>Thanks </strong></h3>\n<h3><strong>for submitting!</strong></h3>'
                }, {
                  type: TEXT,
                  name: "Form Title",
                  properties: {
                    "desktop": {
                      "visible": true,
                      "layout": {
                        "transform": {
                          "translate": "30px, 360px",
                          "rotate": "0deg",
                          "scale": "1,1"
                        },
                        "width": "auto",
                        "height": "auto"
                      },
                      "fontSize": "16",
                      "lineSpacing": "1.2",
                      "letterSpacing": "0",
                      "color": null,
                      "alignment": "left",
                      "animation": null
                    },
                    "mobile": {
                      "visible": true,
                      "layout": {
                        "transform": {
                          "translate": "20px, 415px",
                          "rotate": "0deg",
                          "scale": "1,1"
                        },
                        "width": "238.4921875px",
                        "height": "auto"
                      },
                      "fontSize": "16",
                      "lineSpacing": "1.2",
                      "letterSpacing": "0",
                      "color": null,
                      "alignment": "left",
                      "animation": null
                    }
                  },
                  content: '<p>We’ll get back to you shortly :)</p>'
                },
                {
                  "type": "cleverstory.v2.widgets.image",
                  "name": "Image",
                  "capabilities": null,
                  "order": 1,
                  "fonts": null,
                  "actions": null,
                  "properties": {
                    "desktop": {
                      "visible": true,
                      "shadow": {
                        "color": {
                          "r": 255,
                          "g": 255,
                          "b": 255,
                          "a": 1
                        },
                        "axes": {
                          "x": 0,
                          "y": 0,
                          "b": 0
                        },
                        "show": false
                      },
                      "layout": {
                        "transform": {
                          "translate": "198.0000000000001px,48.99999999999969px",
                          "rotate": "0deg",
                          "scale": "1,1"
                        },
                        "width": "317px",
                        "height": "274.961px"
                      },
                      "crop": null,
                      "alt": null
                    },
                    "mobile": {
                      "visible": true,
                      "layout": {
                        "transform": {
                          "translate": "20px,20px",
                          "rotate": "0deg"
                        },
                        "width": "295px",
                        "height": "256.0584635416667px"
                      },
                      "crop": null,
                      "alt": null
                    }
                  },
                  "content": {
                    "id": imageId,
                    "contentType": "image/png",
                    "type": "image",
                    "width": 500.0,
                    "height": 434.0,
                    "url": `public/cleverstory/v2/media/images/${imageId}/thankyou.png`
                  }
                },
              ],
              order: 2,
            }
            ],
            content: form
          }
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    dispatch(fetchCreatedForms(nextPage, 20, "hubspot"));
  }

  useEffect(() => {
    dispatch(fetchCreatedForms(0, 20, "hubspot"));
  }, []);

  const RenderFormName = ({ id, formName }) => {
    const [tooltip, setTooltip] = useState(false);

    const handleTooltip = (isTruncated) => {
      setTooltip(isTruncated)
    }

    return (
      <Tooltip key={id} placement="top" content={formName} disabled={!tooltip}>
        <Truncate lines={1} width={140} ellipsis="..." onTruncate={handleTooltip}>
          {formName}
        </Truncate>
      </Tooltip>
    )
  }

  const ExternalForms = ({ formType }) => {
    const forms = createdForms.filter(item => {
      return item.type == formType;
    });

    return (
      <>
        <div className={styles.manageForm}>
          <Button variant="primary"
            onClick={handleManageForms}
            className={styles.actionButton}>
            Manage Forms
          </Button>
        </div>
        <InfiniteScroll
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={loadMore}
        >
          <div className={styles.formsCardContainer}>
            {loading && forms.length == 0 &&
              <div className={styles.loadingMessage}>Loading Forms...</div>
            }

            {!loading && forms && forms.length == 0 &&
              <div className={styles.emptyMessage}>
                No Forms Found in your Supplies
              </div>
            }
            {!loading && forms && forms.map(form => {
              return (
                <div>
                  <div className={styles.formsCard} key={form.id} onClick={() => handleCreateExternalForm(form, {})}>
                    <img src="/images/forms/formPreview.svg" alt="" />
                  </div>
                  <div className={styles.formName}>
                    <RenderFormName id={form.id} formName={form.name} />
                  </div>
                </div>
              )
            })}
          </div>
        </InfiniteScroll>
      </>
    )
  }

  return (
    <Tab.Pane eventKey="FORMS" className={cx(styles.tabPane, styles.formsPalette)}>
      <div className={styles.formManagement}>
        <Tab.Container id="regular-form-management-tab" defaultActiveKey="general">
          <header className={styles.formManagementHeader}>
            <div className={styles.headerTitle}>REGULAR FORMS</div>

            <Nav variant="pills" className={styles.formManagementTab}>
              <Nav.Item>
                <Nav.Link eventKey="general">General</Nav.Link>
              </Nav.Item>
            </Nav>

            <>
              <div className={styles.headerTitle}>EXTERNAL FORMS</div>
              <Nav variant="pills" className={styles.formManagementTab}>
                {activeIntegrations.length > 0 && activeIntegrations.map((integration) => {
                  return (
                    <Nav.Item>
                      <Nav.Link eventKey={integration.type}>
                        <div className={styles.logoContainer}>
                          <Image src={`/images/integrations/${integration.type}.png`} />
                          <div id="#contact_form"></div>
                        </div>
                        {integration.type}
                      </Nav.Link>
                    </Nav.Item>
                  )
                })
                }

                {
                  <Nav.Item>
                    <Nav.Link eventKey={'external'}>
                      <div className={styles.logoContainer}>
                        <Image src={`/images/forms/external_form.svg`} />
                      </div>
                      external
                    </Nav.Link>
                  </Nav.Item>
                }
              </Nav>
            </>
          </header>
          <Tab.Content className={styles.formContentContainer}>
            <Tab.Pane eventKey="general">
              <div>{forms.map((form, index) => {
                return (
                  <div className={styles.widget} key={index} onClick={() => {
                    handleCreateForm(form.data);
                  }}>
                    {form.render()}
                  </div>)
              })}</div>
            </Tab.Pane>
            <Tab.Pane eventKey="hubspot">
              <ExternalForms formType={"hubspot"} />
            </Tab.Pane>
            <Tab.Pane eventKey="marketo">
              <ExternalForms formType={"marketo"} />
            </Tab.Pane>
            <Tab.Pane eventKey="pardot">
              <ExternalForms formType={"pardot"} />
            </Tab.Pane>
            <Tab.Pane eventKey="external">
              <ExternalForms formType={"external"} />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </Tab.Pane>
  );
}

const MenusPalette = ({ editorSession, defaultParent }) => {
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const parentLayout = _.get(parentWidget, 'properties.desktop.layout');

  const handleCreateMenu = (data) => {
    const position = getPositionForNewWidget(editorSession, parentLayout, { width: 1200, height: 40 });
    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: MENU,
            name: "Menu",
            properties: {
              ...data.properties,
              desktop: {
                ...data.properties.desktop,
                visible: true,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `${position.x}px, ${position.y}px`,
                    rotate: "0deg"
                  }
                }
              },
              mobile: {
                ...data.properties.mobile,
                visible: true,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: "0px, 20px",
                    rotate: "0deg",
                  }
                }
              },
            },
            widgets: data.widgets,
            content: data.content
          },
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }

  return (
    <Tab.Pane eventKey="MENUS" className={cx(styles.tabPane, styles.menuPalette)}>
      {menus.map((menu, index) => {
        return (
          <div key={index} className={styles.widget} onClick={() => handleCreateMenu(menu.data)}>
            {menu.render()}
          </div>
        );
      })}
    </Tab.Pane>
  );
}


const ChartPalette = ({ editorSession, defaultParent }) => {
  const storyboardContext = React.useContext(StoryboardContext);
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });
  const [inputValue, setInputValue] = React.useState("");
  const [error, setError] = useState(null);

  const breakpoint = useSelector(state => { return _.get(state, `${editorSession}.present.breakpoint`) })
  const parentLayout = _.get(parentWidget, `properties.${breakpoint}.layout`);

  const desktopParentLayout = _.get(parentWidget, `properties.desktop.layout`);
  const mobileParentLayout = _.get(parentWidget, `properties.mobile.layout`);

  const handleCreateAnchorWidget = () => {
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
        mixpanelPayload = {
          ...mixpanelPayload,
          widget_type: "Chart",
          brandkit_widget: false
        }
    dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload));

    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: 700, height: 260 });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 260, height: 260 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: CHARTS,
            name: "Chart",
            properties: {
              "desktop": {
                "visible": true,
                "layout": {
                  "transform": {
                    "translate": `${desktopPosition.x}px, ${desktopPosition.y}px`,
                    "rotate": "0deg"
                  },
                  "width": "700px",
                  "height": "260px"
                }
              },
              "mobile": {
                "visible": true,
                "layout": {
                  "transform": {
                    "translate": `${mobilePosition.x}px, ${mobilePosition.y}px`,
                    "rotate": "0deg"
                  },
                  "width": "260px",
                  "height": "260px"
                }
              }
            },
            content: inputValue,
          }
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }


  const validUrl = (value) => {
    if (!value || value.trim().length === 0) {
      return "Please enter source url";
    }

    if (!(value.startsWith('http://') || value.startsWith('https://'))) {
      return "Url should begin with http or https";
    }

    try {
      const urlObject = new URL(value);
    } catch (e) {
      return "Please enter source url";
    }

    return null;
  }

  const handleChange = (e) => {
    var value = e.target.value;
    setInputValue(e.target.value);

    const error = validUrl(value);
    setError(error);
  }

  return (
    <Tab.Pane eventKey="CHART" className={cx(styles.tabPane, styles.chartPalette)}>
      <div className={cx(styles.widget, styles.embed)} >
        <div className={styles.details}>
          <p>Paste a link of your chart.</p>
        </div>

        <div className={styles.inputContainer}>
          {/* <input value={inputValue} onChange={handleChange} } type="text" placeholder="e.g. https://charts.cleverstory.com/chart/12345" /> */}
          <Property.Input className={styles.inputField} value={inputValue}
          onChange={handleChange}
          placeholder="Enter Url"
          error={error} />
          <Button disabled={error || !inputValue} onClick={handleCreateAnchorWidget} className={styles.embedButton} variant='secondary'>Embed</Button>
        </div>
        <div className={styles.chartInfo}>
          <div className={styles.details}>
            <p>Read more about embedding charts</p>
            <div className={styles.buttonContainer}>
              <Button className={styles.evervizButton} variant='secondary' href="https://www.everviz.com/" target="_blank">
                <Image className={styles.logo} src="/images/everviz.png" alt="everviz" />
              </Button>
              <Button className={styles.infogramButton} variant='secondary' href="https://infogram.com/" target="_blank">
                <Image className={styles.logo} src="/images/infogram.png" alt="infogram" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Tab.Pane>
  );
}

const AnchorPalette = ({ editorSession, defaultParent }) => {
  const storyboardContext = React.useContext(StoryboardContext);
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });
  const breakpoint = useSelector(state => { return _.get(state, `${editorSession}.present.breakpoint`) })
  const parentLayout = _.get(parentWidget, `properties.${breakpoint}.layout`);

  const handleCreateAnchorWidget = () => {
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
        mixpanelPayload = {
          ...mixpanelPayload,
          widget_type: "Anchor",
          brandkit_widget: false
        }
        dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))
   
    const position = getPositionForNewWidget(editorSession, parentLayout, { width: 1200, height: 50 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: ANCHOR,
            name: "Anchor",
            properties: {
              "desktop": {
                "visible": true,
                "layout": {
                  "transform": {
                    "translate": `${position.x}px, ${position.y}px`,
                    "rotate": "0deg"
                  },
                  "width": "auto",
                  "height": "auto"
                }
              },
              "mobile": {
                "visible": true,
                "layout": {
                  "transform": {
                    "translate": `${position.x}px, ${position.y}px`,
                    "rotate": "0deg"
                  },
                  "width": "auto",
                  "height": "auto"
                }
              }
            },
            content: null,
          }
        ],
        defaultParent
      )
    );

    paletteContext.close();
  }

  return (
    <Tab.Pane eventKey="ANCHOR" className={cx(styles.tabPane, styles.embedPalette)}>
      <div className={cx(styles.widget, styles.embed)} onClick={handleCreateAnchorWidget}>
        <i className={cx('cleverstory-anchor', styles.icon)}></i>
        <div className={styles.details}>
          <h5>Anchors</h5>
          <p>When you add an Anchor to a section, you can navigate to this section from anywhere on the page using hyperlinks or buttons</p>
        </div>
      </div>
    </Tab.Pane>
  );
}